import type { WorkspaceTreeCategoryNode, WorkspaceTreeFolderNode } from '../../../api/types';

export interface WorkspaceFolderCrumb {
    name: string;
    folderPath: string;
}

export interface WorkspaceFolderMatch {
    categoryNode: WorkspaceTreeCategoryNode;
    folderNode: WorkspaceTreeFolderNode;
    ancestors: WorkspaceFolderCrumb[];
}

export function normalizeWorkspaceTreePath(path?: string | null): string {
    return (path || '').replace(/\/+$/, '').trim();
}

export function pathsEqual(a?: string | null, b?: string | null): boolean {
    return normalizeWorkspaceTreePath(a) === normalizeWorkspaceTreePath(b);
}

export function flattenFolderNodes(
    nodes: WorkspaceTreeFolderNode[],
    ancestors: WorkspaceFolderCrumb[] = [],
): Array<{ node: WorkspaceTreeFolderNode; ancestors: WorkspaceFolderCrumb[] }> {
    const out: Array<{ node: WorkspaceTreeFolderNode; ancestors: WorkspaceFolderCrumb[] }> = [];
    for (const node of nodes) {
        out.push({ node, ancestors });
        const nextAncestors = [...ancestors, { name: node.name, folderPath: node.folder_path }];
        out.push(...flattenFolderNodes(node.children || [], nextAncestors));
    }
    return out;
}

export function findFolderMatchByPath(
    tree: WorkspaceTreeCategoryNode[],
    folderPath?: string | null,
): WorkspaceFolderMatch | null {
    const target = normalizeWorkspaceTreePath(folderPath);
    if (!target) return null;
    for (const categoryNode of tree) {
        // 1. Check manual folders (including nested)
        for (const entry of flattenFolderNodes(categoryNode.folders || [])) {
            if (pathsEqual(entry.node.folder_path, target)) {
                return {
                    categoryNode,
                    folderNode: entry.node,
                    ancestors: entry.ancestors,
                };
            }
        }
        // 2. Check flat entities
        for (const ent of categoryNode.entities || []) {
            if (pathsEqual(ent.folder_path, target)) {
                return {
                    categoryNode,
                    folderNode: {
                        name: ent.entity,
                        folder_path: ent.folder_path,
                        count: ent.count,
                        children: [],
                    },
                    ancestors: [], // Entities are currently top-level under category
                };
            }
        }
    }
    return null;
}
